import { speakableSentences } from './speakable.js';
import {
  READ_ALOUD_MAX_CHARS,
  READ_ALOUD_MAX_SSML_CHARS,
  isPassageSource,
} from './read-aloud-policy.js';

// XML encoding size is a transport constraint, never a decision about wording.
const xmlSize = (text) =>
  text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;').length;
const fits = (text) =>
  text.length <= READ_ALOUD_MAX_CHARS && xmlSize(text) <= READ_ALOUD_MAX_SSML_CHARS;

function splitLongSentence(part) {
  if (fits(part.text)) return [part];
  const units = [...new Intl.Segmenter('und', { granularity: 'word' }).segment(part.text)].flatMap(
    ({ segment }) => (fits(segment) ? [segment] : Array.from(segment)),
  );
  const result = [];
  let text = '';
  let start = part.start;
  const flush = () => {
    if (text.trim()) result.push({ ...part, text: text.trim(), start, end: start + text.length });
    start += text.length;
    text = '';
  };
  for (const unit of units) {
    if (!fits(text + unit)) flush();
    text += unit;
  }
  flush();
  return result;
}

/** Keep the complete answer together whenever it fits; preserve every spoken word. */
export function readAloudParts(markdown) {
  const result = [];
  let pending = null;
  const flush = () => {
    if (pending) result.push(pending);
    pending = null;
  };
  for (const sentence of speakableSentences(markdown)) {
    if (sentence.cue) {
      flush();
      result.push(sentence);
      continue;
    }
    for (const part of splitLongSentence(sentence)) {
      const separator = pending?.block === part.block ? ' ' : '\n\n';
      const text = pending ? pending.text + separator + part.text : part.text;
      if (pending && !fits(text)) flush();
      if (!pending) pending = { ...part, segments: [{ ...part }] };
      else {
        pending.text = text;
        pending.end = part.end;
        pending.block = part.block;
        pending.segments.push({ ...part });
      }
    }
  }
  flush();
  return result;
}

export const speechParts = (source, text, done) =>
  isPassageSource(source)
    ? readAloudParts(text)
    : speakableSentences(text, { done }).flatMap(splitLongSentence);
